// Host bootstrap — NOT a contract. Contract v2: every app creates its own
// Vue app; remotes' dev harnesses assemble the same pieces from the host
// exposes (./auth, ./remoteRegistry, ./layout).
//
// Baseline global stylesheet, byte-identical with the UI MCP scaffold template
// (templates/base/src/styles/global.css).
import "@/shared/styles/global.css";
// Document-level icon font, as MerchantApp's shell has it: shadow-DOM remotes
// (the admin bar) @import primeicons inside their shadow root, where @font-face
// never registers, so the face has to exist in the document.
import "primeicons/primeicons.css";
import { createApp } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import { ShipBobTelemetry } from "@shipbob/telemetry";
import App from "@/App.vue";
import { createWaypoint } from "@shipbob/waypoint";
import { loadAppSettings } from "@/shared/config/app-settings";
import { getShellRouter } from "@/router";
import { installLegacyShadowStyles } from "@/shared/legacy";
import {
  applyRemoteEntryOverrides,
  configureRemotes,
  defaultRemotes,
  preloadRemoteRoutes,
  registerI18n,
  registerLegacyRemoteRoutes,
} from "@/shared/federation/remote-registry";
import enUS from "@/shared/locales/en-US.json";

async function bootstrap() {
  const settings = await loadAppSettings();
  // Code-defined catalog + per-environment entry-URL overrides.
  configureRemotes(applyRemoteEntryOverrides(defaultRemotes, settings.remoteEntries));
  const router = getShellRouter();
  // Before app.use(router): the router resolves the initial location on install,
  // and legacy prefixes must already be routes by then (see the function's doc).
  await registerLegacyRemoteRoutes(router);

  const app = createApp(App);
  app.use(createPinia());

  // Before any legacy remote mounts (see shadowStyles.ts): shadow-DOM remotes
  // need PrimeVue's head sheets cloned in, and in dev their mount wrapper must
  // find the host's own sheets already marked.
  installLegacyShadowStyles();

  // PrimeVue theme and options from @shipbob/waypoint, no options passed (CLAUDE.md rule 20).
  createWaypoint(app);

  // Single host i18n instance (singleton like the router). Locale naming
  // matches the remote template's locale files (en-US.json). Remotes in
  // routes-mode contribute messages via their ./i18nMessages expose.
  const i18n = createI18n({
    legacy: false,
    locale: "en-US",
    fallbackLocale: "en-US",
    messages: { "en-US": enUS },
  });
  app.use(i18n);
  registerI18n(i18n);

  app.use(router);

  // Host-owned Application Insights. No POC counterpart — this is the one
  // piece of the deleted app-factory that nothing else replaces. An empty
  // connection string (the committed local-dev default) skips telemetry;
  // deploy substitutes the real value.
  if (settings.telemetry?.enabled && settings.telemetry.connectionString) {
    const logger = new ShipBobTelemetry({
      connectionString: settings.telemetry.connectionString,
      tags: {
        "ai.cloud.role": "MerchantAppHost",
      },
      router,
    });
    app.provide("logger", logger);
  }

  app.mount("#app");

  void preloadRemoteRoutes(router);
}

void bootstrap();
